import dayjs from "dayjs";

// App streams existed from 22 Jun 2026 but carried only a trickle of traffic until the app
// v10.14 rollout; daily app sessions went ~10 → 220+ between 29 Aug and 1 Sep 2026.
export const APP_TRACKING_START = dayjs("2026-08-30");

export function dayCount([start, end]) {
  return end.startOf("day").diff(start.startOf("day"), "day") + 1;
}

export function rangeFromStart(start, days) {
  const s = start.startOf("day");
  return [s, s.add(days - 1, "day")];
}

// The exact immediately preceding period of the same length — e.g. Sep 1-30 -> Aug 2-31.
// No gap, nothing excluded: this is "vs last 30 days" literally. It won't generally start
// on the same weekday as the current range; see weekdayAlignTrend for how the day-by-day
// trend charts handle that without touching this range (or any total computed from it).
export function previousRange(range) {
  const days = dayCount(range);
  return rangeFromStart(range[0].startOf("day").subtract(days, "day"), days);
}

// Trims a pair of equal-length day lists (current vs previous, both from datesOf()) so
// every paired index falls on the same weekday in both, WITHOUT changing either period's
// real date range — this is purely a display concern for the day-by-day trend charts.
// Since previousDates is always exactly `length` days before currentDates, previousDates's
// start is always `length % 7` weekdays "behind" currentDates's start; dropping that many
// days off the front of previousDates and off the back of currentDates realigns them
// (0 days trimmed when the range is already a multiple of 7 — already aligned).
export function weekdayAlignTrend(currentDates, previousDates) {
  const shift = currentDates.length % 7;
  if (shift === 0) return { current: currentDates, previous: previousDates };
  return {
    current: currentDates.slice(0, currentDates.length - shift),
    previous: previousDates.slice(shift),
  };
}

// The comparison range always has the same number of days as the main range.
export function compareRangeFor({ range, compareMode, compareStart }) {
  if (compareMode === "custom" && compareStart) return rangeFromStart(compareStart, dayCount(range));
  return previousRange(range);
}

export function formatRange([start, end]) {
  const sameYear = start.year() === end.year();
  return `${start.format(sameYear ? "D MMM" : "D MMM YYYY")} – ${end.format("D MMM YYYY")}`;
}

// pct is null when there is no baseline; isNew flags "appeared from nothing".
export function delta(current, previous) {
  const cur = Number(current) || 0;
  if (previous == null) return { change: null, pct: null, isNew: false };
  const prev = Number(previous) || 0;
  return { change: cur - prev, pct: prev > 0 ? (cur - prev) / prev : null, isNew: prev === 0 && cur > 0 };
}

// Joins two row lists on `key`: current-period order first, then keys seen only in the previous period.
export function zipBy(key, currentRows, previousRows) {
  const prevByKey = new Map((previousRows || []).map((r) => [r[key], r]));
  const seen = new Set();
  const out = [];
  for (const cur of currentRows || []) {
    seen.add(cur[key]);
    out.push({ key: cur[key], current: cur, previous: prevByKey.get(cur[key]) });
  }
  for (const prev of previousRows || []) {
    if (!seen.has(prev[key])) out.push({ key: prev[key], current: undefined, previous: prev });
  }
  return out;
}

// Labels that aren't a tenant; they only appear when "Hide untagged" is off.
const UNTAGGED_LABELS = new Set(["unknown", "(not set)", ""]);
export const isTenantLabel = (label) => !UNTAGGED_LABELS.has(String(label ?? "").toLowerCase());

// rows: [{ client, now, before }] (active users per tenant in each period).
export function tenantAdoption(rows, topN = 5) {
  const tenants = rows.filter((r) => isTenantLabel(r.client));
  const shareOfTop = (key) => {
    const values = tenants.map((r) => r[key]).sort((a, b) => b - a);
    const total = values.reduce((s, v) => s + v, 0);
    return total > 0 ? values.slice(0, topN).reduce((s, v) => s + v, 0) / total : null;
  };
  return {
    activeNow: tenants.filter((r) => r.now > 0).length,
    activeBefore: tenants.filter((r) => r.before > 0).length,
    started: tenants.filter((r) => r.now > 0 && r.before === 0).sort((a, b) => b.now - a.now),
    stopped: tenants.filter((r) => r.before > 0 && r.now === 0).sort((a, b) => b.before - a.before),
    topShareNow: shareOfTop("now"),
    topShareBefore: shareOfTop("before"),
    // The actual tenants behind topShareNow, so the UI can list who they are.
    topTenantsNow: [...tenants].filter((r) => r.now > 0).sort((a, b) => b.now - a.now).slice(0, topN),
  };
}

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

// Average of a daily metric per weekday; days with no row count as zero.
export function weekdayAverages(dailyRows, [start, end], metric) {
  const byDate = new Map((dailyRows || []).map((r) => [r.date, Number(r[metric]) || 0]));
  const sums = new Array(7).fill(0);
  const counts = new Array(7).fill(0);
  for (let d = start.startOf("day"); !d.isAfter(end, "day"); d = d.add(1, "day")) {
    const i = (d.day() + 6) % 7;
    sums[i] += byDate.get(d.format("YYYY-MM-DD")) || 0;
    counts[i] += 1;
  }
  return WEEKDAYS.map((weekday, i) => ({ weekday, value: counts[i] ? sums[i] / counts[i] : null }));
}

const APP_DEVICE_LABELS = new Set(["iOS app", "Android app"]);

// deviceRows: device-breakdown response ([{ device, sessions }]).
export function webAppSplit(deviceRows) {
  let web = 0;
  let app = 0;
  for (const r of deviceRows || []) {
    if (APP_DEVICE_LABELS.has(r.device)) app += Number(r.sessions) || 0;
    else web += Number(r.sessions) || 0;
  }
  return { web, app, total: web + app };
}

// rows: [{ ..., change }]. Largest increases and largest decreases.
export function topMovers(rows, n = 10) {
  const ups = rows.filter((r) => r.change > 0).sort((a, b) => b.change - a.change).slice(0, n);
  const downs = rows.filter((r) => r.change < 0).sort((a, b) => a.change - b.change).slice(0, n);
  return { ups, downs };
}
